import { useEffect, useState } from "react";
import { Check, Zap, Loader2, Sparkles } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const PLANS = [
  { id: "preview", name: "Preview", monthly: 0, yearly: 0, blurb: "See where you stand — free.",
    features: ["Business profile & identity", "Funding readiness score", "Personal & business credit ranges"] },
  { id: "track", name: "Track", monthly: 44.99, yearly: 35.99, blurb: "Monitor & start building.",
    features: ["Everything in Preview", "Business credit monitoring — Experian, Equifax, D&B & TransUnion", "Rent reporting to build credit", "Utility & bill reporting", "Vendor tradeline tracking"] },
  { id: "build", name: "Build", monthly: 54.99, yearly: 43.99, popular: true, blurb: "Actively build business credit.",
    features: ["Everything in Track", "Credit Builder Account — adds a tradeline", "Done-for-you funding application prep", "Bookkeeping & cash-flow tools", "Priority support"] },
  { id: "expand", name: "Expand", monthly: 79.99, yearly: 63.99, blurb: "Get funded & scale.",
    features: ["Everything in Build", "Funding marketplace & lender matching", "Dedicated funding concierge", "FICO SBSS tracking (7,500+ lenders)", "Capital passport sharing"] },
];

export default function Billing() {
  const [interval, setInterval] = useState("month");
  const [current, setCurrent] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const loadPlan = () => api.get("/payments/my-plan").then(({ data }) => setCurrent(data.plan)).catch(() => setCurrent("Preview"));
  useEffect(() => { loadPlan(); }, []);
  if (current === null) return <Loading />;

  const subscribe = async (plan) => {
    if (plan.id === "preview") return;
    setLoadingId(plan.id);
    try {
      const lookup_key = `${plan.id}_${interval === "month" ? "monthly" : "yearly"}`;
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="billing-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Membership &amp; Billing</h1>
        <p className="mt-1 text-[14px] text-slate-500">Choose the plan that fits where you are. Cancel anytime.</p>
      </div>

      <Card className="flex flex-wrap items-center justify-between gap-3 p-5" data-testid="current-plan-card">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Current plan</div>
          <div className="font-head text-xl font-extrabold text-slate-900" data-testid="current-plan-name">{current}</div>
        </div>
        <div className="inline-flex items-center rounded-full border border-slate-200 p-1">
          <button onClick={() => setInterval("month")} data-testid="interval-monthly"
            className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${interval === "month" ? "bg-[#5B4BE6] text-white" : "text-slate-500"}`}>Monthly</button>
          <button onClick={() => setInterval("year")} data-testid="interval-annual"
            className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${interval === "year" ? "bg-[#5B4BE6] text-white" : "text-slate-500"}`}>Annual · save 20%</button>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {PLANS.map((p) => {
          const price = interval === "month" ? p.monthly : p.yearly;
          const isCurrent = current === p.name;
          return (
            <Card key={p.id} className={`flex flex-col p-5 ${p.popular ? "border-[#b0862f] ring-1 ring-[#b0862f]" : ""}`} data-testid={`plan-${p.id}`}>
              {p.popular && <div className="mb-2 inline-flex w-fit items-center gap-1 rounded-md bg-[#b0862f]/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#b0862f]"><Sparkles size={10} /> Most popular</div>}
              <div className="font-head text-[15px] font-bold text-slate-900">{p.name}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-head text-3xl font-extrabold text-slate-900">${price}</span>
                <span className="text-[12px] text-slate-400">{p.id === "preview" ? "free" : interval === "month" ? "/mo" : "/mo billed annually"}</span>
              </div>
              <p className="mt-1 text-[12px] text-slate-500">{p.blurb}</p>
              <div className="mt-4 flex-1 space-y-2">
                {p.features.map((f) => (
                  <div key={f} className="flex items-start gap-2 text-[12px] text-slate-600"><Check size={14} className="mt-0.5 shrink-0 text-emerald-500" />{f}</div>
                ))}
              </div>
              <button onClick={() => subscribe(p)} disabled={isCurrent || p.id === "preview" || loadingId === p.id} data-testid={`subscribe-${p.id}`}
                className={`mt-4 inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-[13px] font-bold transition-colors ${isCurrent ? "cursor-default bg-emerald-50 text-emerald-600" : p.id === "preview" ? "cursor-default bg-slate-100 text-slate-400" : "bg-[#5B4BE6] text-white hover:bg-[#1a5fd0]"}`}>
                {loadingId === p.id && <Loader2 size={14} className="animate-spin" />}
                {isCurrent ? <><Zap size={13} /> Active</> : p.id === "preview" ? "Free" : "Subscribe"}
              </button>
            </Card>
          );
        })}
      </div>
      <p className="text-center text-[12px] text-slate-400">Payments are securely processed by Stripe. Test mode — use card 4242 4242 4242 4242.</p>
    </div>
  );
}
